import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsMeeting } from './api';
import { Modal, inputCls, btnPrimary, btnGhost } from './shared';

// Post-meeting prompt queue. An hour after a synced meeting ends (only
// meetings held after the mailbox was connected), the next visit to the ATS
// asks how it went — one prompt after another if several have piled up.
// Logging writes a meeting note on the person and Jev suggests the follow-up
// task from what was written; Skip clears a prompt without logging.
const SNOOZE_KEY = 'ats-outcome-prompts-at';
const SNOOZE_MS = 30 * 60 * 1000;

const OutcomePrompts: React.FC = () => {
  const { token } = useAuth();
  const [queue, setQueue] = useState<AtsMeeting[]>([]);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    try {
      const last = Number(window.sessionStorage.getItem(SNOOZE_KEY) || 0);
      if (Date.now() - last < SNOOZE_MS) return;
    } catch { /* storage unavailable — just ask */ }
    atsFetch(token, '/calendar')
      .then((d) => {
        if (Array.isArray(d.awaitingOutcome) && d.awaitingOutcome.length) {
          setQueue(d.awaitingOutcome);
          try { window.sessionStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch { /* ok */ }
        }
      })
      .catch(() => {});
  }, [token]);

  const meeting = queue[idx];
  if (!meeting) return null;

  const advance = () => {
    setText(''); setBusy(false);
    setIdx((i) => i + 1);
    window.setTimeout(() => setNote(''), 4000);
  };

  const log = async () => {
    if (!token || busy) return;
    setBusy(true);
    try {
      const d = await atsFetch(token, `/calendar/${meeting.id}/outcome`, { method: 'POST', body: JSON.stringify({ body: text.trim() }) });
      if (d.suggestion) setNote(`Jev suggested: "${d.suggestion.title}" — accept it from the person's record or the ATS Overview.`);
      advance();
    } catch { setBusy(false); }
  };

  const skip = async () => {
    if (!token || busy) return;
    setBusy(true);
    await atsFetch(token, `/calendar/${meeting.id}/skip`, { method: 'POST' }).catch(() => {});
    advance();
  };

  const endedAt = meeting.ends_at ? new Date(meeting.ends_at).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <Modal title={`How did it go?${queue.length > 1 ? `  (${idx + 1} of ${queue.length})` : ''}`} onClose={() => setQueue([])}>
      <p className="text-sm font-medium text-gray-900">
        {meeting.subject || 'Meeting'}{meeting.person_name ? ` — ${meeting.person_name}` : ''}
      </p>
      <p className="text-xs text-gray-400 mb-3">Ended {endedAt}</p>
      {note && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 mb-3">{note}</p>}
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="What happened? e.g. strong call — they want a shortlist by Friday, chase PO next week"
        rows={3}
        className={inputCls}
        autoFocus
      />
      <p className="text-[11px] text-gray-400 mt-1.5 mb-4">This is saved as a meeting note on their record, and Jev turns any next step you mention into a suggested task.</p>
      <div className="flex items-center justify-between">
        <button onClick={() => setQueue([])} className="text-xs text-gray-400 hover:text-gray-600">Ask me later</button>
        <div className="flex gap-2">
          <button onClick={skip} disabled={busy} className={btnGhost}>Skip</button>
          <button onClick={log} disabled={busy || !text.trim()} className={btnPrimary}>
            {busy ? 'Saving…' : queue.length - idx > 1 ? 'Log & next' : 'Log outcome'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OutcomePrompts;
